import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Avatar, Button } from '../../components/ui';
import { HUDDLE_TEXT } from './huddleApi';
import { useHuddleCall, useLiveHuddles } from './HuddleProvider';
import type { PeerView } from './mesh';
import { formatElapsed, isActive, recordingLine, startButtonLabel } from './useHuddle';
import './huddles.css';

/**
 * The huddle's screens (h1-plan.md section 3, h1h2-plan.md 0.2 and 3,
 * mockup-huddle.html:26-36). Every word on them is a text node (traps.md:67):
 * names come from the database and are never parsed as HTML.
 */

function tileStatus(peer: PeerView): string {
  if (peer.status === 'failed') return "Can't connect";
  if (peer.status === 'joining') return 'joining...';
  if (peer.muted) return 'muted';
  return peer.speaking ? 'talking' : 'listening';
}

export function HuddleTiles({
  me,
  muted,
  peers,
}: {
  me: string;
  muted: boolean;
  peers: PeerView[];
}) {
  return (
    <ul className="huddle-tiles" aria-label="People in the huddle">
      <li className="huddle-tile">
        <Avatar name={me} size="sm" />
        <span className="huddle-tile-name">{me} (you)</span>
        <span className="huddle-tile-state">{muted ? 'muted' : 'listening'}</span>
      </li>
      {peers.map((peer) => (
        <li
          key={peer.sessionId}
          className={`huddle-tile${peer.speaking && !peer.muted ? ' huddle-tile--talking' : ''}${
            peer.status === 'failed' ? ' huddle-tile--failed' : ''
          }`}
        >
          <Avatar name={peer.name || 'Someone'} size="sm" />
          <span className="huddle-tile-name">{peer.name || 'Someone'}</span>
          <span className="huddle-tile-state">{tileStatus(peer)}</span>
        </li>
      ))}
    </ul>
  );
}

function useElapsed(since: number | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  return since === null ? '00:00' : formatElapsed(now - since);
}

/**
 * The bar under the room header while you are in THIS room's call: notice,
 * tiles, Mute, Leave, "n of 12" and the timer.
 */
export function HuddleBar({ roomId, me }: { roomId: string; me: string }) {
  const call = useHuddleCall();
  const { state, peers } = call;
  const elapsed = useElapsed(
    state.phase === 'in_call' || state.phase === 'leaving' ? state.joinedAt : null,
  );
  if (state.roomId !== roomId) return null;
  if (state.phase !== 'in_call' && state.phase !== 'leaving') return null;

  const line = recordingLine(state.recording);
  const count = peers.length + 1;
  return (
    <section className="huddle-bar" aria-label="Huddle">
      <p
        className={`huddle-notice${state.recording === 'recording' ? '' : ' huddle-notice--warn'}`}
        role="status"
        title="Use headphones so others do not hear themselves."
      >
        {state.recording === 'recording' ? (
          <span className="huddle-dot" aria-hidden="true" />
        ) : null}
        {line}
      </p>
      <HuddleTiles me={me} muted={state.muted} peers={peers} />
      {state.message ? <p role="alert">{state.message}</p> : null}
      <div className="huddle-controls">
        <Button
          variant="secondary"
          size="sm"
          onClick={call.toggleMute}
          disabled={state.phase === 'leaving'}
          aria-pressed={state.muted}
        >
          {state.muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={call.leave}
          disabled={state.phase === 'leaving'}
        >
          {state.phase === 'leaving' ? HUDDLE_TEXT.saving : 'Leave'}
        </Button>
        <span className="huddle-meta">
          {elapsed} · {count} of {state.max}
        </span>
      </div>
    </section>
  );
}

/**
 * Start / Join / Full in the room header (h1-plan.md:219), hidden for people
 * who cannot post (a Viewer) and for the Alex Huddle view (the page does not
 * mount it there). Also shows the pre-join notice (plan 0.2) and the one line
 * a failed attempt leaves behind (mic refused, full, replaced, connection lost).
 */
export function HuddleStartButton(props: { roomId: string; roomName: string; canPost: boolean }) {
  // Outside a provider (or for a Viewer) nothing mounts, so no query runs.
  const call = useHuddleCall();
  if (!call.available || !props.canPost) return null;
  return <StartButtonInner {...props} />;
}

function StartButtonInner({ roomId, roomName }: { roomId: string; roomName: string }) {
  const call = useHuddleCall();
  const { state } = call;
  const inThisRoom = state.roomId === roomId;
  const busyHere = inThisRoom && (state.phase === 'in_call' || state.phase === 'leaving');
  const live = useLiveHuddles(!busyHere);
  // Hidden until the first answer: a database without huddles yet (or a
  // refused rpc) must not flash a button that then vanishes.
  if (live.isError || live.isPending) return null;
  if (busyHere) return null;

  const here = live.data?.get(roomId);
  const joiningHere = inThisRoom && state.phase === 'joining';
  const elsewhere = isActive(state.phase) && !inThisRoom;
  const full = here !== undefined && here.activeCount >= state.max;
  const label = joiningHere ? 'Joining...' : startButtonLabel(here, state.max);
  const message = inThisRoom ? state.message : null;

  return (
    <div className="huddle-start">
      <Button
        size="sm"
        variant={here === undefined ? 'primary' : 'secondary'}
        disabled={joiningHere || elsewhere || full}
        title={elsewhere ? `You are in a huddle in ${state.roomName}. Leave it first.` : undefined}
        onClick={() => call.join(roomId, roomName)}
      >
        {label}
      </Button>
      <p className="huddle-start-notice">{HUDDLE_TEXT.beforeJoin}</p>
      {message === null ? null : (
        <p className="huddle-start-error" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}

/** The green "Huddle" mark on a room row (mockup-huddle.html:21). */
export function RoomHuddleDot({ roomId }: { roomId: string }) {
  const call = useHuddleCall();
  if (!call.available) return null;
  return <RoomHuddleDotInner roomId={roomId} />;
}

function RoomHuddleDotInner({ roomId }: { roomId: string }) {
  const live = useLiveHuddles();
  if (live.isError || live.data?.has(roomId) !== true) return null;
  return (
    <span className="huddle-room-dot" aria-label="Huddle in progress">
      Huddle
    </span>
  );
}

/**
 * Outside the room while in a call: a small "In huddle: <room> · Leave" pill
 * in the shell (h1-plan.md:227). Clicking the room name goes back to it.
 * Hidden on the room itself, where the bar already shows the call.
 */
export function HuddleShellPill() {
  const call = useHuddleCall();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = call;
  if (state.roomId === null) return null;
  if (state.phase !== 'in_call' && state.phase !== 'leaving') return null;
  const roomId = state.roomId;
  const openRoom = new URLSearchParams(location.search).get('room');
  if (location.pathname === '/messages' && openRoom === roomId) return null;
  return (
    <div className="huddle-pill" role="status">
      <button
        type="button"
        className="huddle-pill-room"
        onClick={() => {
          void navigate(`/messages?room=${encodeURIComponent(roomId)}`);
        }}
      >
        <span className="huddle-dot" aria-hidden="true" />
        In huddle: {state.roomName}
      </button>
      <span aria-hidden="true">·</span>
      <span>{recordingLine(state.recording)}</span>
      {state.message ? <span role="alert">{state.message}</span> : null}
      <Button
        variant="secondary"
        size="sm"
        onClick={call.toggleMute}
        disabled={state.phase === 'leaving'}
        aria-pressed={state.muted}
      >
        {state.muted ? 'Unmute' : 'Mute'}
      </Button>
      <button
        type="button"
        className="huddle-pill-leave"
        onClick={call.leave}
        disabled={state.phase === 'leaving'}
      >
        {state.phase === 'leaving' ? HUDDLE_TEXT.saving : 'Leave'}
      </button>
    </div>
  );
}
